/**
 * CarryFromDialog — §13.7's fourth Verify assertion ("the carry-from dialog
 * lists topics from the chosen study and posts the selection"), session 4
 * FACTS 8. Three steps in one `ui/Modal`, no routing: pick the previous
 * study, pick its executed CTA, pick the topics to carry.
 *
 * Both carry-from routes are DEFERRED TO A MOUNT STEP (session 4 DECISION f)
 * — in production today they answer 404, and this dialog renders that
 * loudly (`describeClausesApiError`'s "not mounted yet" message) rather than
 * swallowing the rejection (G13).
 *
 * Mirrors the `ApplyRateCardDialog` / `ApplyPreviousStudyDialog` precedent
 * (WorkspaceTab, WP-55): the result renders INSIDE the still-open dialog
 * (never an auto-close), `onCarried` (the clause-list refetch) fires as soon
 * as the response arrives, and the person closes the dialog themselves once
 * they have seen the outcome.
 */
import { useEffect, useState } from 'react';
import Modal from '../../../ui/Modal';
import EmptyState from '../../../ui/EmptyState';
import FilterSelect from '../../../ui/FilterSelect';
import { primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { listStudies, listNegotiations } from '../../api/client';
import type { Study, Negotiation } from '../../api/client';
import { listNegotiationVersions } from '../../api/workspace';
import type { ContractsVersion } from '../../api/workspace';
import {
  carryFromVersion,
  describeClausesApiError,
  getCarryFromPreview,
  type CarryFromPreview,
  type CarryFromResult,
} from '../../api/clauses';
import { topicLabel } from './topics';

interface CarryFromDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The version being reviewed (the tab's own `?versionId=`) — the TARGET of the carry. */
  targetVersionId: string;
  /** Runs the version-level review (the same action the tab's own "Run review" button fires) — offered inline when the preview reports `target.reviewed === false`. */
  onRunReview: () => void | Promise<void>;
  /** Refetches the clause list — fired as soon as the carry POST resolves (before the person closes the dialog), same as WorkspaceTab's Apply dialogs. */
  onCarried: () => void;
}

const EXECUTABLE_NEGOTIATION_KINDS = new Set(['contract', 'amendment_contract']);

export function CarryFromDialog({ isOpen, onClose, targetVersionId, onRunReview, onCarried }: CarryFromDialogProps) {
  const { toast } = useToast();

  const [studies, setStudies] = useState<Study[]>([]);
  const [studyId, setStudyId] = useState('');
  const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
  const [negotiationId, setNegotiationId] = useState('');
  const [versions, setVersions] = useState<ContractsVersion[]>([]);
  const [sourceVersionId, setSourceVersionId] = useState('');

  const [preview, setPreview] = useState<CarryFromPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<CarryFromResult | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    listStudies().then(setStudies).catch(() => setStudies([]));
  }, [isOpen]);

  useEffect(() => {
    if (!studyId) {
      setNegotiations([]);
      return;
    }
    listNegotiations(studyId)
      .then((rows) => setNegotiations(rows.filter((n) => EXECUTABLE_NEGOTIATION_KINDS.has(n.kind))))
      .catch(() => setNegotiations([]));
  }, [studyId]);

  useEffect(() => {
    if (!negotiationId) {
      setVersions([]);
      return;
    }
    listNegotiationVersions(negotiationId)
      .then((rows) => setVersions(rows.filter((v) => v.is_executed)))
      .catch(() => setVersions([]));
  }, [negotiationId]);

  useEffect(() => {
    if (!sourceVersionId) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    setSelectedTopics(new Set());
    setResult(null);
    getCarryFromPreview(targetVersionId, sourceVersionId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(
            describeClausesApiError('GET', `/api/contracts/versions/${targetVersionId}/carry-from/${sourceVersionId}`, err)
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetVersionId, sourceVersionId]);

  function reset() {
    setStudyId('');
    setNegotiationId('');
    setSourceVersionId('');
    setPreview(null);
    setPreviewError(null);
    setSelectedTopics(new Set());
    setResult(null);
    setSubmitError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function toggleTopic(topic: string, checked: boolean) {
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      if (checked) next.add(topic);
      else next.delete(topic);
      return next;
    });
  }

  async function handleSubmit() {
    if (!sourceVersionId || selectedTopics.size === 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await carryFromVersion(targetVersionId, sourceVersionId, [...selectedTopics]);
      setResult(res);
      toast(`Carried ${res.counts.carried} of ${res.counts.requested_topics} topic(s).`, {
        type: res.counts.carried > 0 ? 'success' : 'info',
      });
      onCarried();
    } catch (err) {
      const message = describeClausesApiError(
        'POST',
        `/api/contracts/versions/${targetVersionId}/carry-from/${sourceVersionId}`,
        err
      );
      setSubmitError(message);
      toast(message, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Apply clause language from a previous study" maxWidth="max-w-3xl">
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-end gap-2">
          <FilterSelect
            label="Previous study"
            auto
            value={studyId}
            onChange={(v) => {
              setStudyId(v);
              setNegotiationId('');
              setSourceVersionId('');
            }}
            options={[
              { value: '', label: 'Choose a study…' },
              ...studies.map((s) => ({ value: s.id, label: s.protocol_number })),
            ]}
          />
          <FilterSelect
            label="Executed CTA"
            auto
            disabled={!studyId}
            value={negotiationId}
            onChange={(v) => {
              setNegotiationId(v);
              setSourceVersionId('');
            }}
            options={[
              { value: '', label: 'Choose a negotiation…' },
              ...negotiations.map((n) => ({ value: n.id, label: n.label ?? n.kind })),
            ]}
          />
          <FilterSelect
            label="Executed version"
            auto
            disabled={!negotiationId}
            value={sourceVersionId}
            onChange={setSourceVersionId}
            options={[
              { value: '', label: 'Choose a version…' },
              ...versions.map((v) => ({ value: v.id, label: `#${v.version_no} — ${v.version_date} — round ${v.round ?? '—'}` })),
            ]}
          />
        </div>

        {studyId && negotiations.length === 0 && (
          <EmptyState title="No executed CTA" hint="This study has no contract negotiation to carry language from." variant="card" />
        )}
        {negotiationId && versions.length === 0 && (
          <EmptyState title="No executed CTA" hint="This negotiation has no executed version yet." variant="card" />
        )}

        {previewLoading && <p className="text-xs text-gray-500 dark:text-gray-400">Loading topics…</p>}
        {previewError && (
          <p data-testid="ctr-error" className="text-sm text-red-600 dark:text-red-400">
            {previewError}
          </p>
        )}

        {preview && !result && (
          <>
            {!preview.target.reviewed && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs dark:border-amber-800 dark:bg-amber-900/20">
                <span className="text-amber-800 dark:text-amber-300">
                  This version has no clause review yet ({preview.target.review_count} reviewed) — carrying needs a review first.
                </span>
                <button type="button" className={secondaryBtnCls} onClick={() => onRunReview()}>
                  Run review
                </button>
              </div>
            )}

            <div data-testid="ctr-carry-topics" className="max-h-80 space-y-1.5 overflow-y-auto">
              {preview.topics.map((t) => {
                // A topic is disabled for being genuinely unpairable only once the
                // target HAS a review (§9.3): before that every topic trivially
                // has zero target clauses (DECISION for this dialog — the person
                // must still be able to pick topics and submit, so the server's
                // own CTA_CLAUSE_CARRY_NO_REVIEW 409 is what actually stops them,
                // never a client-side guess masking it).
                const disabled = t.topic === 'unassigned' || (preview.target.reviewed && t.pairs.length === 0);
                const reason =
                  t.topic === 'unassigned'
                    ? "Never carryable — this clause's topic could not be assigned."
                    : !preview.target.reviewed
                      ? null
                      : t.source_clauses.length === 0
                        ? `The source version has no clause of topic '${t.topic}'.`
                        : t.target_clauses.length === 0
                          ? `The version being reviewed has no clause of topic '${t.topic}'.`
                          : t.pairs.length === 0
                            ? `${t.unpaired_source.length} source and ${t.unpaired_target.length} target clause(s) of this topic could not be paired 1:1.`
                            : null;
                const sourceClause = t.source_clauses[0];
                const targetClause = t.target_clauses[0];
                return (
                  <label
                    key={t.topic}
                    className={`flex items-start gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700 ${
                      disabled ? 'opacity-60' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`ctr-carry-topic-${t.topic}`}
                      disabled={disabled}
                      checked={selectedTopics.has(t.topic)}
                      onChange={(e) => toggleTopic(t.topic, e.target.checked)}
                      className="mt-0.5"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-gray-800 dark:text-gray-100" title={t.topic}>
                        {topicLabel(t.topic)}
                      </span>{' '}
                      <span className="text-xs text-gray-500 dark:text-gray-400">({t.pairs.length} pairable)</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Source: {sourceClause ? `${sourceClause.path ?? '—'} — ${sourceClause.excerpt}` : '—'}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Target: {targetClause ? `${targetClause.path ?? '—'} — ${targetClause.excerpt}` : '—'}
                      </span>
                      {reason && <span className="block text-xs text-amber-700 dark:text-amber-400">{reason}</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {submitError && (
          <p data-testid="ctr-error" className="text-sm text-red-600 dark:text-red-400">
            {submitError}
          </p>
        )}

        {result && (
          <div data-testid="ctr-carry-result" className="space-y-2 text-xs">
            <p className="tabular-nums text-gray-700 dark:text-gray-300">
              Requested {result.counts.requested_topics}, carried {result.counts.carried}, skipped {result.counts.skipped}.
            </p>
            {result.carried.map((c) => (
              <div
                key={c.clause_review_id}
                className="rounded-lg border border-primary-200 bg-primary-50 p-2 dark:border-primary-800 dark:bg-primary-900/20"
              >
                <span className="font-medium">{topicLabel(c.topic)}</span> — {c.carried_from.clause_path ?? '—'}{' '}
                <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  Proposed, not accepted
                </span>
              </div>
            ))}
            {result.skipped.map((s, idx) => (
              <div key={idx} className="rounded-lg border border-gray-200 p-2 text-gray-600 dark:border-gray-700 dark:text-gray-300">
                <span className="font-medium">{topicLabel(s.topic)}</span> skipped — {s.reason}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              data-testid="ctr-carry-submit"
              className={primaryBtnCls}
              disabled={!sourceVersionId || selectedTopics.size === 0 || submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Carrying…' : `Carry ${selectedTopics.size || ''} topic${selectedTopics.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CarryFromDialog;
